<template>
  <div class="inv-page">
    <div class="inv-toolbar">
      <div class="inv-toolbar__title">出入库填报</div>
      <div class="inv-toolbar__actions">
        <el-button size="small" type="primary" @click="openCreateItemDialog">添加库存商品</el-button>
        <el-upload
          class="inv-upload"
          :show-file-list="false"
          :before-upload="handleImportExcel"
          accept=".xls,.xlsx"
        >
          <el-button size="small">导入Excel</el-button>
        </el-upload>
        <el-button size="small" @click="exportExcel">导出Excel</el-button>
        <el-button size="small" type="text" @click="handleDownloadTemplate">下载模板</el-button>
        <el-button size="small" @click="dialogVisible = true">手动填报</el-button>
      </div>
    </div>

    <!-- 打码枪扫码台 -->
    <div class="inv-scan-card">
      <div class="inv-scan-card__head">
        <span class="inv-scan-card__label">打码枪出入库</span>
        <span class="inv-scan-card__hint">光标保持在扫码框，扫码后回车自动提交</span>
      </div>
      <div class="inv-scan-row">
        <div class="inv-mode-switch">
          <button
            type="button"
            class="inv-mode-btn"
            :class="{ active: scanMode === 'in' }"
            @click="scanMode = 'in'"
          >
            入库
          </button>
          <button
            type="button"
            class="inv-mode-btn"
            :class="{ active: scanMode === 'out' }"
            @click="scanMode = 'out'"
          >
            出库
          </button>
        </div>
        <el-input
          ref="scanInput"
          v-model="scanCode"
          class="inv-scan-input"
          size="medium"
          clearable
          placeholder="扫条形码或手动输入后回车"
          @keyup.enter.native="onScanEnter"
        />
        <el-input-number
          v-model="scanQty"
          :min="1"
          :step="1"
          size="medium"
          controls-position="right"
          class="inv-scan-qty"
        />
        <el-button
          type="primary"
          size="medium"
          :loading="scanBusy"
          @click="onScanEnter"
        >
          {{ scanMode === 'in' ? '确认入库' : '确认出库' }}
        </el-button>
      </div>
      <div v-if="lastScanTip" class="inv-scan-tip" :class="{ err: lastScanErr }">{{ lastScanTip }}</div>
    </div>

    <!-- 筛选 + 列表同一卡片 -->
    <div class="inv-panel">
      <div class="inv-filter-bar">
        <el-select v-model="filters.region" size="small" placeholder="使用区域" style="width: 120px;" @change="search">
          <el-option label="全部区域" value="all" />
          <el-option v-for="r in regions" :key="r" :label="r" :value="r" />
        </el-select>
        <el-input
          v-model="filters.keyword"
          size="small"
          clearable
          prefix-icon="el-icon-search"
          placeholder="商品名 / 条形码"
          style="width: 200px;"
          @keyup.enter.native="search"
        />
        <el-input
          v-model="filters.operator"
          size="small"
          clearable
          placeholder="操作人"
          style="width: 120px;"
          @keyup.enter.native="search"
        />
        <el-button type="primary" size="small" icon="el-icon-search" @click="search">筛选</el-button>
        <el-button size="small" @click="reset">重置</el-button>
      </div>

      <el-table
        :data="pagedSummary"
        border
        stripe
        size="small"
        class="inv-table"
        style="width: 100%;"
        empty-text="暂无库存商品，请先添加或导入"
      >
        <el-table-column prop="productName" label="商品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="barcode" label="条形码" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.barcode || '—' }}</template>
        </el-table-column>
        <el-table-column prop="usageArea" label="区域" width="100" />
        <el-table-column prop="currentStock" label="库存" width="80" />
        <el-table-column prop="unit" label="单位" width="64" />
        <el-table-column label="最低阈值" width="88">
          <template slot-scope="scope">
            {{ scope.row.warningThreshold != null && scope.row.warningThreshold !== '' ? scope.row.warningThreshold : '—' }}
          </template>
        </el-table-column>
        <el-table-column prop="lastOperator" label="最近操作人" width="100" show-overflow-tooltip />
        <el-table-column prop="lastOperationTime" label="最近时间" width="150">
          <template slot-scope="scope">{{ $formatDateTime(scope.row.lastOperationTime) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="168" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="quickFill(scope.row, 'in')">入库</el-button>
            <el-button type="text" size="small" @click="quickFill(scope.row, 'out')">出库</el-button>
            <el-button type="text" size="small" @click="openAdjustDialog(scope.row)">改库存</el-button>
          </template>
        </el-table-column>
      </el-table>

      <admin-pagination
        :total="total"
        :page.sync="currentPage"
        :page-size.sync="pageSize"
        @change="onPaginationChange"
      />
    </div>

    <!-- 新增库存商品 -->
    <el-dialog
      title="添加库存商品"
      :visible.sync="createItemVisible"
      width="520px"
      append-to-body
      custom-class="inv-compact-dialog"
      @closed="resetCreateForm"
      @opened="focusCreateBarcode"
    >
      <el-form :model="createForm" label-width="100px" size="small" class="inv-compact-form">
        <el-form-item label="商品名称" required>
          <el-input v-model="createForm.name" placeholder="必填" maxlength="80" show-word-limit />
        </el-form-item>
        <el-form-item label="条形码">
          <el-input
            ref="createBarcode"
            v-model="createForm.barcode"
            placeholder="可扫码录入，选填"
            maxlength="64"
          />
        </el-form-item>
        <el-form-item label="使用区域" required>
          <el-select v-model="createForm.usageArea" placeholder="请选择" style="width: 100%;">
            <el-option v-for="r in regions" :key="r" :label="r" :value="r" />
          </el-select>
        </el-form-item>
        <el-form-item label="单位" required>
          <el-select v-model="createForm.unit" placeholder="请选择" filterable style="width: 100%;">
            <el-option v-for="u in units" :key="u" :label="u" :value="u" />
          </el-select>
        </el-form-item>
        <el-form-item label="初始库存" required>
          <el-input-number v-model="createForm.quantity" :min="0" :step="1" controls-position="right" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="最低阈值">
          <el-input-number v-model="createForm.minThreshold" :min="0" :step="1" controls-position="right" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="createForm.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
        <el-form-item label="操作人">
          <el-input v-model="createForm.operator" placeholder="默认当前用户" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="createItemVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="createSaving" @click="submitCreateItem">保存</el-button>
      </span>
    </el-dialog>

    <!-- 修改库存 -->
    <el-dialog
      title="修改库存"
      :visible.sync="adjustVisible"
      width="440px"
      append-to-body
      custom-class="inv-compact-dialog"
      @closed="adjustRow = null"
    >
      <el-form v-if="adjustRow" label-width="100px" size="small" class="inv-compact-form">
        <el-form-item label="商品名称">
          <el-input :value="adjustRow.productName" disabled />
        </el-form-item>
        <el-form-item label="条形码">
          <el-input :value="adjustRow.barcode || '—'" disabled />
        </el-form-item>
        <el-form-item label="当前库存">
          <el-input :value="String(adjustRow.currentStock)" disabled />
        </el-form-item>
        <el-form-item label="调整后库存" required>
          <el-input-number v-model="adjustForm.newQty" :min="0" :step="1" controls-position="right" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="调整原因">
          <el-select v-model="adjustForm.reason" placeholder="选填" style="width: 100%;" clearable>
            <el-option label="盘点修正" value="盘点修正" />
            <el-option label="损耗" value="损耗" />
            <el-option label="其他" value="其他" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="adjustForm.remark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
        <el-form-item label="操作人">
          <el-input v-model="adjustForm.operator" />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="adjustVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="adjustSaving" @click="submitAdjust">确定</el-button>
      </span>
    </el-dialog>

    <!-- 手动出入库 -->
    <el-dialog
      title="手动出入库填报"
      :visible.sync="dialogVisible"
      width="520px"
      custom-class="inv-compact-dialog"
      append-to-body
      @opened="focusScanIfNeeded"
    >
      <el-tabs v-model="activeTab" class="lvyun-tabs">
        <el-tab-pane label="入库" name="in">
          <el-form :model="inboundForm" label-width="88px" size="small" class="inv-compact-form">
            <el-form-item label="商品" required>
              <el-select
                v-model="inboundForm.itemId"
                placeholder="请选择门店库存商品"
                filterable
                clearable
                style="width: 100%;"
                @change="handleInboundStockRefresh"
              >
                <el-option
                  v-for="p in products"
                  :key="p.id"
                  :label="productOptionLabel(p)"
                  :value="p.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item v-if="selectedInboundProduct" label="信息">
              <span class="inv-meta">
                {{ selectedInboundProduct.usageArea }} · {{ selectedInboundProduct.unit }}
                <template v-if="selectedInboundProduct.barcode"> · 条码 {{ selectedInboundProduct.barcode }}</template>
              </span>
            </el-form-item>
            <el-form-item label="数量" required>
              <el-input-number v-model="inboundForm.quantity" :min="1" controls-position="right" />
              <span class="inv-stock-inline">当前库存 {{ inboundCurrentStock }}</span>
            </el-form-item>
            <el-form-item label="操作人">
              <el-input v-model="inboundForm.operator" />
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="inboundForm.remark" type="textarea" :rows="2" placeholder="可选" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
        <el-tab-pane label="出库" name="out">
          <el-form :model="outboundForm" label-width="88px" size="small" class="inv-compact-form">
            <el-form-item label="商品" required>
              <el-select
                v-model="outboundForm.itemId"
                placeholder="请选择门店库存商品"
                filterable
                clearable
                style="width: 100%;"
                @change="handleOutboundStockRefresh"
              >
                <el-option
                  v-for="p in products"
                  :key="p.id"
                  :label="productOptionLabel(p)"
                  :value="p.id"
                />
              </el-select>
            </el-form-item>
            <el-form-item v-if="selectedOutboundProduct" label="信息">
              <span class="inv-meta">
                {{ selectedOutboundProduct.usageArea }} · {{ selectedOutboundProduct.unit }}
                <template v-if="selectedOutboundProduct.barcode"> · 条码 {{ selectedOutboundProduct.barcode }}</template>
              </span>
            </el-form-item>
            <el-form-item label="数量" required>
              <el-input-number v-model="outboundForm.quantity" :min="1" controls-position="right" />
              <span class="inv-stock-inline" :class="{ warn: isOutOverStock }">
                当前库存 {{ outboundCurrentStock }}
                <template v-if="isOutOverStock">（不足）</template>
              </span>
            </el-form-item>
            <el-form-item label="操作人">
              <el-input v-model="outboundForm.operator" />
            </el-form-item>
            <el-form-item label="备注">
              <el-input v-model="outboundForm.remark" type="textarea" :rows="2" placeholder="可选" />
            </el-form-item>
          </el-form>
        </el-tab-pane>
      </el-tabs>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :disabled="isConfirmDisabled" @click="submitCurrent">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchInventoryRegions,
  fetchInventoryUnits,
  fetchInventoryRows,
  fetchInventoryProducts,
  fetchCurrentStock,
  lookupInventoryByBarcode,
  submitInbound,
  submitOutbound,
  createInventoryItem,
  adjustInventoryItem,
  downloadInventoryTemplate,
  exportInventoryExcel,
  importInventoryExcel
} from '../api/inventory'
import { getUserInfo } from '../utils/auth'
import { onInventoryChanged, notifyInventoryChanged } from '../utils/inventorySync'
import { guardUploadFile, formatImportErrors as formatImportErrorsText, showImportResult } from '../utils/fileImport'

const SYNC_SOURCE = 'InventoryInOutReport'

export default {
  name: 'InventoryInOutReport',
  data() {
    const userInfo = getUserInfo()
    const defaultOperator = userInfo && userInfo.username ? userInfo.username : '管理员'
    return {
      _unsubInventorySync: null,
      activeTab: 'in',
      dialogVisible: false,
      defaultOperator,
      scanMode: 'in',
      scanCode: '',
      scanQty: 1,
      scanBusy: false,
      lastScanTip: '',
      lastScanErr: false,

      regions: [],
      units: [],
      products: [],

      filters: {
        region: 'all',
        operator: '',
        keyword: ''
      },

      summaryRowsAll: [],
      pagedSummary: [],
      total: 0,
      pageSize: 30,
      currentPage: 1,

      createItemVisible: false,
      createSaving: false,
      createForm: {
        name: '',
        barcode: '',
        usageArea: '',
        unit: '',
        quantity: 0,
        minThreshold: undefined,
        remark: '',
        operator: defaultOperator
      },

      adjustVisible: false,
      adjustSaving: false,
      adjustRow: null,
      adjustForm: {
        newQty: 0,
        reason: '盘点修正',
        remark: '',
        operator: defaultOperator
      },

      inboundForm: {
        itemId: '',
        quantity: 1,
        operator: defaultOperator,
        remark: ''
      },
      outboundForm: {
        itemId: '',
        quantity: 1,
        operator: defaultOperator,
        remark: ''
      },

      inboundCurrentStock: 0,
      outboundCurrentStock: 0
    }
  },
  computed: {
    selectedInboundProduct() {
      return (this.products || []).find((p) => p.id === this.inboundForm.itemId) || null
    },
    selectedOutboundProduct() {
      return (this.products || []).find((p) => p.id === this.outboundForm.itemId) || null
    },
    isOutOverStock() {
      if (this.activeTab !== 'out') return false
      const q = Number(this.outboundForm.quantity)
      if (!this.outboundForm.itemId) return false
      if (!Number.isFinite(q) || q <= 0) return false
      return q > this.outboundCurrentStock
    },
    isConfirmDisabled() {
      return this.activeTab === 'out' && this.isOutOverStock
    }
  },
  mounted() {
    this.init()
    this._unsubInventorySync = onInventoryChanged((e) => {
      const src = e && e.detail && e.detail.source
      if (src === SYNC_SOURCE) return
      this.refreshInventoryFromSync()
    })
    this.$nextTick(() => this.focusScanInput())
  },
  beforeDestroy() {
    if (this._unsubInventorySync) {
      this._unsubInventorySync()
      this._unsubInventorySync = null
    }
  },
  methods: {
    focusScanInput() {
      const el = this.$refs.scanInput
      if (el && el.focus) el.focus()
    },
    focusScanIfNeeded() {
      /* dialog open: keep page scan input for gun when dialog closes */
    },
    focusCreateBarcode() {
      this.$nextTick(() => {
        const el = this.$refs.createBarcode
        if (el && el.focus) el.focus()
      })
    },
    async refreshInventoryFromSync() {
      try {
        await this.reloadProductsAndForms()
        await this.fetchSummary()
      } catch (e) {}
    },
    productOptionLabel(p) {
      if (!p) return ''
      const code = p.barcode ? ` · ${p.barcode}` : ''
      return `${p.name}（${p.usageArea || ''}）${code}`
    },

    async init() {
      const regionsResp = await fetchInventoryRegions()
      this.regions = regionsResp && regionsResp.data ? regionsResp.data : []

      const unitsResp = await fetchInventoryUnits()
      this.units = unitsResp && unitsResp.data ? unitsResp.data : []

      await this.reloadProductsAndForms()
      await this.fetchSummary()
    },

    async reloadProductsAndForms() {
      const productsResp = await fetchInventoryProducts()
      this.products = productsResp && productsResp.data ? productsResp.data : []
      if (this.products.length) {
        if (!this.inboundForm.itemId || !this.products.some((p) => p.id === this.inboundForm.itemId)) {
          this.inboundForm.itemId = this.products[0].id
        }
        if (!this.outboundForm.itemId || !this.products.some((p) => p.id === this.outboundForm.itemId)) {
          this.outboundForm.itemId = this.products[0].id
        }
      } else {
        this.inboundForm.itemId = ''
        this.outboundForm.itemId = ''
      }
      await this.handleInboundStockRefresh()
      await this.handleOutboundStockRefresh()
    },

    async onScanEnter() {
      const code = String(this.scanCode || '').trim()
      if (!code) {
        this.lastScanErr = true
        this.lastScanTip = '请先扫码或输入条形码'
        return
      }
      const qty = Math.floor(Number(this.scanQty))
      if (!Number.isFinite(qty) || qty < 1) {
        this.lastScanErr = true
        this.lastScanTip = '数量须为正整数'
        return
      }
      if (this.scanBusy) return
      this.scanBusy = true
      this.lastScanErr = false
      this.lastScanTip = '处理中…'
      try {
        const look = await lookupInventoryByBarcode(code)
        if (!look || look.code !== 200 || !look.data) {
          throw new Error((look && look.message) || '未找到商品')
        }
        const item = look.data
        const op = this.defaultOperator || '管理员'
        if (this.scanMode === 'in') {
          const resp = await submitInbound({
            itemId: item.itemId,
            barcode: code,
            quantity: qty,
            operator: op,
            remark: '打码枪入库'
          })
          if (!(resp && resp.code === 200)) throw new Error((resp && resp.message) || '入库失败')
          this.lastScanTip = `入库成功：${item.productName} +${qty}（库存 ${resp.data && resp.data.currentStock != null ? resp.data.currentStock : '—'}）`
        } else {
          const cur = Number(item.currentStock) || 0
          if (qty > cur) {
            throw new Error(`库存不足：${item.productName} 当前 ${cur}`)
          }
          const resp = await submitOutbound({
            itemId: item.itemId,
            barcode: code,
            quantity: qty,
            operator: op,
            remark: '打码枪出库'
          })
          if (!(resp && resp.code === 200)) throw new Error((resp && resp.message) || '出库失败')
          this.lastScanTip = `出库成功：${item.productName} -${qty}（库存 ${resp.data && resp.data.currentStock != null ? resp.data.currentStock : '—'}）`
        }
        this.scanCode = ''
        await this.reloadProductsAndForms()
        await this.fetchSummary()
        notifyInventoryChanged(SYNC_SOURCE)
      } catch (e) {
        this.lastScanErr = true
        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          (e && e.message) ||
          '扫码处理失败'
        this.lastScanTip = msg
      } finally {
        this.scanBusy = false
        this.$nextTick(() => this.focusScanInput())
      }
    },

    quickFill(row, mode) {
      this.activeTab = mode
      if (mode === 'in') {
        this.inboundForm.itemId = row.itemId || row.productId
        this.inboundForm.quantity = 1
        this.handleInboundStockRefresh()
      } else {
        this.outboundForm.itemId = row.itemId || row.productId
        this.outboundForm.quantity = 1
        this.handleOutboundStockRefresh()
      }
      this.dialogVisible = true
    },

    openCreateItemDialog() {
      this.createForm = {
        name: '',
        barcode: '',
        usageArea: this.regions[0] || '',
        unit: (this.units && this.units[0]) || '个',
        quantity: 0,
        minThreshold: undefined,
        remark: '',
        operator: this.defaultOperator
      }
      this.createItemVisible = true
    },

    resetCreateForm() {
      this.createSaving = false
    },

    async submitCreateItem() {
      const payload = {
        name: this.createForm.name,
        barcode: this.createForm.barcode,
        usageArea: this.createForm.usageArea,
        unit: this.createForm.unit,
        quantity: this.createForm.quantity,
        remark: this.createForm.remark,
        operator: this.createForm.operator || this.defaultOperator
      }
      if (
        this.createForm.minThreshold !== undefined &&
        this.createForm.minThreshold !== null &&
        this.createForm.minThreshold !== ''
      ) {
        payload.minThreshold = this.createForm.minThreshold
      }
      this.createSaving = true
      try {
        const resp = await createInventoryItem(payload)
        if (resp && (resp.code === 200 || resp.code === 201)) {
          this.$message.success(resp.message || '保存成功')
          this.createItemVisible = false
          await this.reloadProductsAndForms()
          await this.fetchSummary()
          notifyInventoryChanged(SYNC_SOURCE)
          this.$nextTick(() => this.focusScanInput())
        } else {
          this.$message.error((resp && resp.message) || '保存失败')
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message ? e.response.data.message : '保存失败'
        this.$message.error(msg)
      } finally {
        this.createSaving = false
      }
    },

    openAdjustDialog(row) {
      this.adjustRow = row
      this.adjustForm = {
        newQty: Number(row.currentStock) || 0,
        reason: '盘点修正',
        remark: '',
        operator: this.defaultOperator
      }
      this.adjustVisible = true
    },

    async submitAdjust() {
      if (!this.adjustRow || !this.adjustRow.itemId) return
      const nq = Math.floor(Number(this.adjustForm.newQty))
      if (!Number.isFinite(nq) || nq < 0) {
        this.$message.warning('调整后库存须为非负整数')
        return
      }
      this.adjustSaving = true
      try {
        const resp = await adjustInventoryItem(this.adjustRow.itemId, {
          newQty: nq,
          reason: this.adjustForm.reason || '其他',
          remark: this.adjustForm.remark,
          operator: this.adjustForm.operator || this.defaultOperator
        })
        if (resp && resp.code === 200) {
          this.$message.success(resp.message || '已更新')
          this.adjustVisible = false
          await this.reloadProductsAndForms()
          await this.fetchSummary()
          notifyInventoryChanged(SYNC_SOURCE)
        } else {
          this.$message.error((resp && resp.message) || '更新失败')
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message ? e.response.data.message : '更新失败'
        this.$message.error(msg)
      } finally {
        this.adjustSaving = false
      }
    },

    applyPagination() {
      const startIdx = (this.currentPage - 1) * this.pageSize
      const endIdx = startIdx + this.pageSize
      this.pagedSummary = (this.summaryRowsAll || []).slice(startIdx, endIdx)
    },

    async fetchSummary() {
      try {
        const rowsResp = await fetchInventoryRows({
          region: this.filters.region,
          category: 'all',
          keyword: this.filters.keyword
        })
        let rows = rowsResp && rowsResp.code === 200 && Array.isArray(rowsResp.data) ? rowsResp.data : []
        const op = (this.filters.operator || '').trim()
        if (op) {
          rows = rows.filter((r) => (r.lastOperator || '').includes(op))
        }
        this.summaryRowsAll = rows
        this.currentPage = 1
        this.total = this.summaryRowsAll.length
        this.applyPagination()
      } catch (e) {
        this.summaryRowsAll = []
        this.total = 0
        this.pagedSummary = []
      }
    },

    async search() {
      await this.fetchSummary()
    },

    async reset() {
      this.filters = { region: 'all', operator: '', keyword: '' }
      await this.fetchSummary()
    },

    onPaginationChange({ page, pageSize }) {
      this.currentPage = page
      this.pageSize = pageSize
      this.applyPagination()
    },

    async handleDownloadTemplate() {
      try {
        await downloadInventoryTemplate()
      } catch (e) {
        this.$message.error('模板下载失败')
      }
    },

    async exportExcel() {
      try {
        await exportInventoryExcel({
          region: this.filters.region,
          keyword: this.filters.keyword
        })
      } catch (e) {
        this.$message.error('导出失败')
      }
    },

    async handleImportExcel(file) {
      if (!guardUploadFile(this, file)) return false
      try {
        const operator = this.defaultOperator || '管理员'
        const resp = await importInventoryExcel(file, operator)
        if (resp && resp.code === 200) {
          showImportResult(this, resp, { successMessage: '导入成功' })
          await this.reloadProductsAndForms()
          await this.fetchSummary()
          notifyInventoryChanged(SYNC_SOURCE)
        } else if (resp && resp.code === 400 && resp.data && resp.data.errors) {
          this.$alert(formatImportErrorsText(resp.data) || resp.message || '导入失败', '导入校验失败', {
            type: 'error'
          })
        } else {
          this.$message.error((resp && resp.message) || '导入失败')
        }
      } catch (e) {
        const data = e && e.response && e.response.data
        if (data && (data.message || (data.data && data.data.errors))) {
          this.$alert(formatImportErrorsText(data.data) || data.message, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((data && data.message) || '导入失败')
        }
      }
      return false
    },

    async refreshStockFor(formType) {
      const target = formType === 'in' ? this.inboundForm : this.outboundForm
      const itemId = target.itemId
      if (!itemId) {
        if (formType === 'in') this.inboundCurrentStock = 0
        else this.outboundCurrentStock = 0
        return 0
      }
      const resp = await fetchCurrentStock(itemId)
      const cur =
        resp && resp.code === 200 && resp.data && Number.isFinite(Number(resp.data.currentStock))
          ? Number(resp.data.currentStock)
          : 0
      if (formType === 'in') this.inboundCurrentStock = cur
      else this.outboundCurrentStock = cur
      return cur
    },

    async handleInboundStockRefresh() {
      return this.refreshStockFor('in')
    },
    async handleOutboundStockRefresh() {
      return this.refreshStockFor('out')
    },

    async submitInbound() {
      if (!this.inboundForm.itemId) {
        this.$message.warning('请先选择商品')
        return
      }
      const quantity = Number(this.inboundForm.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.$message.warning('入库数量不合法')
        return
      }
      await this.handleInboundStockRefresh()
      try {
        const resp = await submitInbound({
          itemId: this.inboundForm.itemId,
          quantity,
          operator: this.inboundForm.operator,
          remark: this.inboundForm.remark
        })
        if (resp && resp.code === 200) {
          this.$message.success('入库提交成功')
          this.dialogVisible = false
          await this.reloadProductsAndForms()
          await this.fetchSummary()
          notifyInventoryChanged(SYNC_SOURCE)
          this.$nextTick(() => this.focusScanInput())
        } else {
          this.$message.error(resp && resp.message ? resp.message : '入库提交失败')
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message ? e.response.data.message : '入库提交失败'
        this.$message.error(msg)
      }
    },

    async submitOutbound() {
      if (!this.outboundForm.itemId) {
        this.$message.warning('请先选择商品')
        return
      }
      const quantity = Number(this.outboundForm.quantity)
      if (!Number.isFinite(quantity) || quantity <= 0) {
        this.$message.warning('出库数量不合法')
        return
      }
      const cur = await this.handleOutboundStockRefresh()
      if (quantity > cur) {
        this.$alert(`库存不足，当前库存：${cur}`, '提示', { type: 'warning' })
        return
      }
      try {
        const resp = await submitOutbound({
          itemId: this.outboundForm.itemId,
          quantity,
          operator: this.outboundForm.operator,
          remark: this.outboundForm.remark
        })
        if (resp && resp.code === 200) {
          this.$message.success('出库提交成功')
          this.dialogVisible = false
          await this.reloadProductsAndForms()
          await this.fetchSummary()
          notifyInventoryChanged(SYNC_SOURCE)
          this.$nextTick(() => this.focusScanInput())
        } else {
          this.$message.error(resp && resp.message ? resp.message : '出库提交失败')
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.message ? e.response.data.message : '出库提交失败'
        this.$message.error(msg)
      }
    },

    async submitCurrent() {
      if (this.activeTab === 'in') return this.submitInbound()
      return this.submitOutbound()
    }
  }
}
</script>

<style scoped>
.inv-page {
  padding: 12px 16px 20px;
}

.inv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.inv-toolbar__title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.inv-toolbar__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.inv-upload {
  display: inline-block;
}

.inv-scan-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.inv-scan-card__head {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.inv-scan-card__label {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.inv-scan-card__hint {
  font-size: 12px;
  color: #94a3b8;
}

.inv-scan-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.inv-mode-switch {
  display: inline-flex;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.inv-mode-btn {
  appearance: none;
  border: 0;
  background: #fff;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
  box-shadow: none;
}

.inv-mode-btn.active {
  background: #ecfdf5;
  color: #047857;
}

.inv-scan-input {
  flex: 1;
  min-width: 220px;
}

.inv-scan-qty {
  width: 110px;
}

.inv-scan-tip {
  margin-top: 8px;
  font-size: 13px;
  color: #047857;
  font-weight: 600;
}

.inv-scan-tip.err {
  color: #be123c;
}

.inv-panel {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.inv-filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.inv-table {
  margin-bottom: 4px;
}

.inv-meta {
  font-size: 13px;
  color: #64748b;
}

.inv-stock-inline {
  margin-left: 10px;
  font-size: 12px;
  color: #64748b;
}

.inv-stock-inline.warn {
  color: #be123c;
  font-weight: 600;
}

.inv-compact-form >>> .el-form-item {
  margin-bottom: 12px;
}
</style>

<style>
.inv-compact-dialog .el-dialog__body {
  padding: 12px 18px 6px;
}

.inv-compact-dialog .el-dialog__header {
  padding: 14px 18px 8px;
}

.inv-compact-dialog .el-dialog__footer {
  padding: 8px 18px 14px;
}
</style>
